import type {
  AcceptedRelaxation,
  ProjectDetail,
  ProjectSummary,
  ReplanReason
} from "../features/projects/types";

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | { [key: string]: JsonValue };
export type ProjectRecord = { [key: string]: JsonValue };

export interface ProjectEntityState {
  courses: ProjectRecord[];
  room_types: ProjectRecord[];
  room_instances: ProjectRecord[];
  teachers: ProjectRecord[];
  classes: ProjectRecord[];
  time_slots: ProjectRecord[];
  unavailability: ProjectRecord[];
  plan_requirements: ProjectRecord[];
  plan_allocations: ProjectRecord[];
  teaching_plans: ProjectRecord[];
  timetable_lessons: ProjectRecord[];
  class_course_availability_rules: ProjectRecord[];
  course_non_adjacent_rules: ProjectRecord[];
  course_adjacent_rules: ProjectRecord[];
}

interface ProjectDocumentBase {
  document_revision: number;
  id: string;
  name: string;
  source_project_id: string | null;
  planning_stage: "FOUNDATION" | "DETAIL";
  condition_revision: number;
  timetable_revision: number;
  conditions_locked_at: string | null;
  reference_condition_fingerprint: string | null;
  created_at: string;
  updated_at: string;
  replan_reasons: ReplanReason[];
  entities: ProjectEntityState;
}

export interface ProjectDocumentV1 extends ProjectDocumentBase {
  schema_version: 1;
}

export interface ProjectDocumentV2 extends ProjectDocumentBase {
  schema_version: 2;
}

export interface ProjectDocumentV3 extends ProjectDocumentBase {
  schema_version: 3;
}

export class ProjectDocumentError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = "ProjectDocumentError";
    this.code = code;
  }
}

const LEGACY_ENTITY_KEYS = [
  "courses",
  "room_types",
  "room_instances",
  "teachers",
  "classes",
  "time_slots",
  "unavailability",
  "plan_requirements",
  "plan_allocations",
  "teaching_plans",
  "timetable_lessons"
] as const;

const ENTITY_KEYS: ReadonlyArray<keyof ProjectEntityState> = [
  ...LEGACY_ENTITY_KEYS,
  "class_course_availability_rules",
  "course_non_adjacent_rules",
  "course_adjacent_rules"
];

const WEEK_PATTERN_ENTITY_KEYS = [
  "plan_requirements",
  "plan_allocations",
  "teaching_plans",
  "timetable_lessons"
] as const;
const WEEK_PATTERNS = new Set(["EVERY", "ODD", "EVEN"]);

export function normalizeProjectName(value: string): string {
  const name = value.trim();
  if (!name) {
    throw new ProjectDocumentError("VALIDATION_FAILED", "课表名称不能为空");
  }
  if (name.length > 120) {
    throw new ProjectDocumentError(
      "VALIDATION_FAILED",
      "课表名称不能超过 120 个字符"
    );
  }
  return name;
}

export function createEmptyProjectDocument(
  rawName: string,
  options: { id?: string; now?: string } = {}
): ProjectDocumentV3 {
  const now = options.now ?? new Date().toISOString();
  return {
    schema_version: 3,
    document_revision: 0,
    id: options.id ?? globalThis.crypto.randomUUID(),
    name: normalizeProjectName(rawName),
    source_project_id: null,
    planning_stage: "FOUNDATION",
    condition_revision: 0,
    timetable_revision: 0,
    conditions_locked_at: null,
    reference_condition_fingerprint: null,
    created_at: now,
    updated_at: now,
    replan_reasons: [],
    entities: emptyEntities()
  };
}

export function copyProjectDocument(
  source: ProjectDocumentV3,
  rawName: string,
  options: { id?: string; now?: string; uuid?: () => string } = {}
): ProjectDocumentV3 {
  assertProjectDocument(source);
  const uuid = options.uuid ?? (() => globalThis.crypto.randomUUID());
  const copiedId = options.id ?? uuid();
  const now = options.now ?? new Date().toISOString();
  const idMap = collectEntityIdMap(source.entities, uuid);
  idMap.set(source.id, copiedId);
  const remappedEntities = remapEntities(source.entities, idMap);
  normalizeCoursePairRules(remappedEntities);
  remappedEntities.timetable_lessons = [];
  for (const slot of remappedEntities.time_slots) {
    slot.timetable_active = false;
    slot.timetable_sort_order = null;
  }
  for (const plan of remappedEntities.teaching_plans) {
    delete plan.accepted_relaxations;
  }

  return {
    ...source,
    id: copiedId,
    name: normalizeProjectName(rawName),
    source_project_id: source.id,
    document_revision: 0,
    timetable_revision: 0,
    conditions_locked_at: null,
    reference_condition_fingerprint: null,
    created_at: now,
    updated_at: now,
    replan_reasons: [],
    entities: remappedEntities
  };
}

export function renameProjectDocument(
  source: ProjectDocumentV3,
  rawName: string,
  now = new Date().toISOString()
): ProjectDocumentV3 {
  assertProjectDocument(source);
  return {
    ...source,
    name: normalizeProjectName(rawName),
    document_revision: source.document_revision + 1,
    condition_revision: source.condition_revision + 1,
    updated_at: now
  };
}

export function projectSummary(document: ProjectDocumentV3): ProjectSummary {
  assertProjectDocument(document);
  return {
    id: document.id,
    name: document.name,
    source_project_id: document.source_project_id,
    planning_stage: document.planning_stage,
    document_revision: document.document_revision,
    condition_revision: document.condition_revision,
    timetable_revision: document.timetable_revision,
    conditions_locked_at: document.conditions_locked_at,
    created_at: document.created_at,
    updated_at: document.updated_at
  };
}

export function projectDetail(document: ProjectDocumentV3): ProjectDetail {
  const counts = moduleCounts(document.entities);
  const acceptedRelaxations = acceptedRelaxationsFromPlans(
    document.entities.teaching_plans
  );
  return {
    ...projectSummary(document),
    has_timetable: counts.lessons > 0,
    needs_replan: document.replan_reasons.length > 0,
    replan_reasons: structuredClone(document.replan_reasons),
    module_counts: counts,
    accepted_relaxation_count: acceptedRelaxations.length,
    accepted_relaxations: acceptedRelaxations
  };
}

function acceptedRelaxationsFromPlans(
  plans: ProjectRecord[]
): AcceptedRelaxation[] {
  const events = new Map<string, AcceptedRelaxation>();
  for (const plan of plans) {
    const rows = plan.accepted_relaxations;
    if (!Array.isArray(rows)) continue;
    for (const value of rows) {
      if (!isRecord(value) || typeof value.event_id !== "string") continue;
      if (events.has(value.event_id)) continue;
      events.set(value.event_id, {
        event_id: value.event_id,
        code: typeof value.code === "string" ? value.code : "",
        condition_key:
          typeof value.condition_key === "string" ? value.condition_key : "",
        original_value:
          typeof value.original_value === "string" ? value.original_value : "",
        actual_value:
          typeof value.actual_value === "string" ? value.actual_value : "",
        degree:
          typeof value.degree === "number" && Number.isInteger(value.degree)
            ? value.degree
            : 1,
        message_snapshot:
          typeof value.message_snapshot === "string"
            ? value.message_snapshot
            : ""
      });
    }
  }
  return [...events.values()].sort((left, right) =>
    left.event_id.localeCompare(right.event_id)
  );
}

export function assertProjectDocument(value: unknown): asserts value is ProjectDocumentV3 {
  if (!isRecord(value)) {
    throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档格式不正确");
  }
  if (value.schema_version !== 3) {
    throw new ProjectDocumentError(
      "SCHEMA_UNSUPPORTED",
      "该课表数据版本暂不受当前应用支持"
    );
  }
  if (
    typeof value.id !== "string" ||
    typeof value.name !== "string" ||
    typeof value.document_revision !== "number" ||
    !Number.isInteger(value.document_revision) ||
    value.document_revision < 0 ||
    typeof value.condition_revision !== "number" ||
    !Number.isInteger(value.condition_revision) ||
    value.condition_revision < 0 ||
    typeof value.timetable_revision !== "number" ||
    !Number.isInteger(value.timetable_revision) ||
    value.timetable_revision < 0 ||
    (value.planning_stage !== "FOUNDATION" && value.planning_stage !== "DETAIL") ||
    typeof value.created_at !== "string" ||
    typeof value.updated_at !== "string" ||
    !Array.isArray(value.replan_reasons) ||
    !isRecord(value.entities)
  ) {
    throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档缺少必要字段");
  }
  for (const key of ENTITY_KEYS) {
    if (!Array.isArray(value.entities[key])) {
      throw new ProjectDocumentError("DOCUMENT_INVALID", `课表文档缺少 ${key}`);
    }
  }
  const entities = value.entities as unknown as ProjectEntityState;
  validateAssistantTeacherArrays(entities);
  for (const key of WEEK_PATTERN_ENTITY_KEYS) {
    for (const row of entities[key]) {
      if (
        row.week_pattern !== undefined &&
        (typeof row.week_pattern !== "string" || !WEEK_PATTERNS.has(row.week_pattern))
      ) {
        throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档上课周次不正确");
      }
    }
  }
}

function validateAssistantTeacherArrays(entities: ProjectEntityState): void {
  for (const key of ["plan_allocations", "teaching_plans", "timetable_lessons"] as const) {
    for (const row of entities[key]) {
      const assistantTeacherIds = row.assistant_teacher_ids;
      if (
        !isRecord(row) ||
        "assistant_teacher_id" in row ||
        !Array.isArray(assistantTeacherIds) ||
        assistantTeacherIds.length > 3 ||
        assistantTeacherIds.some((item) => typeof item !== "string") ||
        new Set(assistantTeacherIds).size !== assistantTeacherIds.length ||
        assistantTeacherIds.includes(row.main_teacher_id)
      ) {
        throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档助教字段不正确");
      }
    }
  }
}

export function migrateProjectDocument(value: unknown): ProjectDocumentV3 {
  if (!isRecord(value)) {
    throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档格式不正确");
  }
  if (value.schema_version === 3) {
    const migrated = structuredClone(value);
    if (
      isRecord(migrated.entities) &&
      migrated.entities.course_adjacent_rules === undefined
    ) {
      migrated.entities.course_adjacent_rules = [];
    }
    assertProjectDocument(migrated);
    normalizeUnlockedLegacyCopy(migrated);
    normalizeStaleCompiledPlanConstraints(migrated.entities);
    return withDefaultWeekPatterns(migrated);
  }
  if (value.schema_version === 2) {
    assertProjectDocumentV2(value);
    const migrated = structuredClone(value);
    const result = withDefaultWeekPatterns({
      ...migrated,
      schema_version: 3 as const,
      entities: {
        ...migrated.entities,
        class_course_availability_rules: [],
        course_non_adjacent_rules: [],
        course_adjacent_rules: []
      }
    });
    normalizeUnlockedLegacyCopy(result);
    normalizeStaleCompiledPlanConstraints(result.entities);
    assertProjectDocument(result);
    return result;
  }
  assertProjectDocumentV1(value);
  const migrated = structuredClone(value);
  const entities = migrated.entities;
  for (const key of ["plan_allocations", "teaching_plans", "timetable_lessons"] as const) {
    entities[key] = entities[key].map((row) => {
      const assistantTeacherId = row.assistant_teacher_id;
      const next = { ...row };
      delete next.assistant_teacher_id;
      next.assistant_teacher_ids =
        typeof assistantTeacherId === "string" ? [assistantTeacherId] : [];
      return next;
    });
  }
  const result = withDefaultWeekPatterns({
    ...migrated,
    schema_version: 3 as const,
    entities: {
      ...entities,
      class_course_availability_rules: [],
      course_non_adjacent_rules: [],
      course_adjacent_rules: []
    }
  });
  normalizeUnlockedLegacyCopy(result);
  normalizeStaleCompiledPlanConstraints(result.entities);
  assertProjectDocument(result);
  return result;
}

function normalizeUnlockedLegacyCopy(document: ProjectDocumentV3): void {
  if (
    typeof document.source_project_id !== "string" ||
    document.conditions_locked_at !== null ||
    document.entities.timetable_lessons.length === 0
  ) {
    return;
  }
  document.timetable_revision = 0;
  document.reference_condition_fingerprint = null;
  document.replan_reasons = [];
  document.entities.timetable_lessons = [];
  for (const slot of document.entities.time_slots) {
    slot.timetable_active = false;
    slot.timetable_sort_order = null;
  }
  for (const plan of document.entities.teaching_plans) {
    delete plan.accepted_relaxations;
  }
}

function normalizeStaleCompiledPlanConstraints(entities: ProjectEntityState): void {
  const groups = new Map<string, ProjectRecord[]>();
  for (const plan of entities.teaching_plans) {
    if (plan.is_active === false || typeof plan.id !== "string") continue;
    const groupKey =
      typeof plan.allocation_group_key === "string"
        ? plan.allocation_group_key
        : plan.id;
    groups.set(groupKey, [...(groups.get(groupKey) ?? []), plan]);
  }
  for (const plans of groups.values()) {
    const compiled = plans.some((plan) =>
      [
        "weekly_lessons_min",
        "weekly_lessons_max",
        "allocation_group_required_lessons",
        "source_requirement_id",
        "source_allocation_id"
      ].some((key) => key in plan)
    );
    if (!compiled) continue;
    const current = plans.map((plan) => plan.weekly_lessons);
    if (
      current.some(
        (value) =>
          typeof value !== "number" ||
          !Number.isInteger(value) ||
          value < 1 ||
          value > 15
      )
    ) {
      continue;
    }
    const weeklyLessons = current as number[];
    const outsideRange = plans.some((plan) => {
      const minimum = plan.weekly_lessons_min;
      const maximum = plan.weekly_lessons_max;
      return (
        typeof minimum === "number" &&
        Number.isInteger(minimum) &&
        typeof maximum === "number" &&
        Number.isInteger(maximum) &&
        !(
          minimum <= (plan.weekly_lessons as number) &&
          (plan.weekly_lessons as number) <= maximum
        )
      );
    });
    const requiredValues = new Set(
      plans
        .map((plan) => plan.allocation_group_required_lessons)
        .filter(
          (value): value is number =>
            typeof value === "number" && Number.isInteger(value)
        )
    );
    const required = weeklyLessons.reduce((total, value) => total + value, 0);
    const requiredMismatch =
      requiredValues.size > 0 &&
      (requiredValues.size !== 1 || [...requiredValues][0] !== required);
    const classCourseMismatch =
      new Set(plans.map((plan) => `${plan.class_id}\u0000${plan.course_id}`)).size !== 1;
    if (!outsideRange && !requiredMismatch && !classCourseMismatch) continue;
    plans.forEach((plan, index) => {
      const lessons = weeklyLessons[index];
      plan.weekly_lessons_min = lessons;
      plan.weekly_lessons_max = lessons;
      if (classCourseMismatch) {
        plan.allocation_group_key = plan.id;
        plan.allocation_group_required_lessons = lessons;
        plan.source_requirement_id = null;
        plan.source_allocation_id = null;
      } else {
        plan.allocation_group_required_lessons = required;
      }
    });
  }
}

function withDefaultWeekPatterns(document: ProjectDocumentV3): ProjectDocumentV3 {
  for (const key of WEEK_PATTERN_ENTITY_KEYS) {
    document.entities[key] = document.entities[key].map((row) => ({
      ...row,
      week_pattern: row.week_pattern ?? "EVERY"
    }));
  }
  return document;
}

function assertProjectDocumentV2(value: unknown): asserts value is ProjectDocumentV2 {
  assertProjectDocumentShape(value, 2);
  const entities = value.entities as ProjectEntityState;
  validateAssistantTeacherArrays(entities);
}

function assertProjectDocumentV1(value: unknown): asserts value is ProjectDocumentV1 {
  assertProjectDocumentShape(value, 1);
  for (const key of ["plan_allocations", "teaching_plans", "timetable_lessons"] as const) {
    for (const row of value.entities[key]) {
      if (
        !isRecord(row) ||
        (row.assistant_teacher_id !== undefined &&
          row.assistant_teacher_id !== null &&
          typeof row.assistant_teacher_id !== "string")
      ) {
        throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档助教字段不正确");
      }
    }
  }
}

function assertProjectDocumentShape(
  value: unknown,
  schemaVersion: 1 | 2
): asserts value is ProjectDocumentV1 | ProjectDocumentV2 {
  if (!isRecord(value)) {
    throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档格式不正确");
  }
  if (value.schema_version !== schemaVersion) {
    throw new ProjectDocumentError("SCHEMA_UNSUPPORTED", "该课表数据版本暂不受当前应用支持");
  }
  if (
    typeof value.id !== "string" ||
    typeof value.name !== "string" ||
    typeof value.document_revision !== "number" ||
    !Number.isInteger(value.document_revision) ||
    value.document_revision < 0 ||
    typeof value.condition_revision !== "number" ||
    !Number.isInteger(value.condition_revision) ||
    value.condition_revision < 0 ||
    typeof value.timetable_revision !== "number" ||
    !Number.isInteger(value.timetable_revision) ||
    value.timetable_revision < 0 ||
    (value.planning_stage !== "FOUNDATION" && value.planning_stage !== "DETAIL") ||
    typeof value.created_at !== "string" ||
    typeof value.updated_at !== "string" ||
    !Array.isArray(value.replan_reasons) ||
    !isRecord(value.entities)
  ) {
    throw new ProjectDocumentError("DOCUMENT_INVALID", "课表文档缺少必要字段");
  }
  for (const key of LEGACY_ENTITY_KEYS) {
    if (!Array.isArray(value.entities[key])) {
      throw new ProjectDocumentError("DOCUMENT_INVALID", `课表文档缺少 ${key}`);
    }
  }
}

function emptyEntities(): ProjectEntityState {
  return {
    courses: [],
    room_types: [],
    room_instances: [],
    teachers: [],
    classes: [],
    time_slots: [],
    unavailability: [],
    plan_requirements: [],
    plan_allocations: [],
    teaching_plans: [],
    timetable_lessons: [],
    class_course_availability_rules: [],
    course_non_adjacent_rules: [],
    course_adjacent_rules: []
  };
}

function normalizeCoursePairRules(entities: ProjectEntityState): void {
  for (const row of entities.course_non_adjacent_rules) {
    const left = row.course_a_id;
    const right = row.course_b_id;
    if (typeof left === "string" && typeof right === "string" && left > right) {
      row.course_a_id = right;
      row.course_b_id = left;
    }
  }
  for (const row of entities.course_adjacent_rules) {
    const left = row.course_a_id;
    const right = row.course_b_id;
    if (typeof left !== "string" || typeof right !== "string" || left <= right) continue;
    row.course_a_id = right;
    row.course_b_id = left;
    if (row.order === "A_BEFORE_B") row.order = "B_BEFORE_A";
    else if (row.order === "B_BEFORE_A") row.order = "A_BEFORE_B";
  }
}

function moduleCounts(entities: ProjectEntityState): Record<string, number> {
  const active = (rows: ProjectRecord[], flag = "is_active") =>
    rows.filter((row) => row[flag] !== false).length;
  return {
    courses: active(entities.courses),
    room_types: active(entities.room_types),
    teachers: active(entities.teachers),
    classes: active(entities.classes),
    time_slots: active(entities.time_slots, "condition_active"),
    unavailability: entities.unavailability.length,
    plans: active(entities.teaching_plans),
    plan_requirements: entities.plan_requirements.length,
    plan_allocations: entities.plan_allocations.length,
    lessons: entities.timetable_lessons.length
  };
}

function collectEntityIdMap(
  entities: ProjectEntityState,
  uuid: () => string
): Map<string, string> {
  const mapping = new Map<string, string>();
  for (const key of ENTITY_KEYS) {
    for (const row of entities[key]) {
      if (typeof row.id === "string") mapping.set(row.id, uuid());
    }
  }
  return mapping;
}

function remapEntities(
  entities: ProjectEntityState,
  mapping: ReadonlyMap<string, string>
): ProjectEntityState {
  return Object.fromEntries(
    ENTITY_KEYS.map((key) => [
      key,
      entities[key].map((row) => remapValue(row, mapping) as ProjectRecord)
    ])
  ) as unknown as ProjectEntityState;
}

function remapValue(value: JsonValue, mapping: ReadonlyMap<string, string>): JsonValue {
  if (typeof value === "string") {
    let result = mapping.get(value) ?? value;
    for (const [oldId, newId] of mapping) {
      if (result.includes(oldId)) result = result.replaceAll(oldId, newId);
    }
    return result;
  }
  if (Array.isArray(value)) return value.map((item) => remapValue(item, mapping));
  if (isRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        key === "source_id" ? item : remapValue(item as JsonValue, mapping)
      ])
    );
  }
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
